import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { Card, CardContent } from '../components/ui/card';
import { ArrowSquareOut, BookOpen, CloudRain, Drop, ChartBar, Sparkle } from '@phosphor-icons/react';

/**
 * /credits — Data Sources & Credits page.
 *
 * Public, citable receipts for every academic paper, extension bulletin,
 * weather feed, and open dataset TurfPulse relies on. The page exists so
 * power users (and skeptical FB commenters) can verify that every threshold
 * we ship — drought floor, dollar-spot pressure, FAO-56 ET₀, dewpoint band —
 * traces back to peer-reviewed work or a public-domain government feed.
 */

const Citation = ({ authors, year, title, journal, url, doi, note, testid }) => (
  <li
    className="rounded-lg border border-zinc-200 bg-white p-4 hover:border-emerald-400 transition"
    data-testid={testid}
  >
    <div className="text-xs font-mono text-zinc-500">
      {authors} ({year})
    </div>
    <div className="mt-1 font-semibold text-zinc-900 leading-snug">{title}</div>
    {journal && <div className="mt-1 text-sm italic text-zinc-700">{journal}</div>}
    {note && <div className="mt-2 text-xs text-zinc-600 leading-relaxed">{note}</div>}
    <div className="mt-3 flex flex-wrap gap-3 text-xs">
      {doi && (
        <a
          href={`https://doi.org/${doi}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-mono text-emerald-700 hover:text-emerald-900 underline"
        >
          DOI: {doi} <ArrowSquareOut size={12} />
        </a>
      )}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 underline"
        >
          Read paper <ArrowSquareOut size={12} />
        </a>
      )}
    </div>
  </li>
);

const FeedRow = ({ name, what, url, license, testid }) => (
  <tr data-testid={testid}>
    <td className="px-4 py-3 font-semibold text-zinc-900 align-top">{name}</td>
    <td className="px-4 py-3 text-sm text-zinc-700 align-top">{what}</td>
    <td className="px-4 py-3 text-xs font-mono text-zinc-600 align-top whitespace-nowrap">
      {license}
    </td>
    <td className="px-4 py-3 align-top">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-emerald-700 underline hover:text-emerald-900"
      >
        Source <ArrowSquareOut size={11} />
      </a>
    </td>
  </tr>
);

export default function DataSourcesPage() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-zinc-50 to-white"
      data-testid="data-sources-page"
    >
      <SEO
        title="Data Sources & Credits — TurfPulse"
        description="Every academic paper, extension bulletin, weather feed, and open dataset TurfPulse uses to make calls. Open citations, public URLs."
        canonical="https://getturfpulse.com/credits"
      />
      <BreadcrumbJsonLd
        items={[
          { label: 'Home', path: '/' },
          { label: 'Data Sources & Credits', path: '/credits' },
        ]}
      />

      {/* Hero */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
            <BookOpen size={14} weight="fill" /> RECEIPTS
          </div>
          <h1 className="mt-4 text-4xl font-black tracking-tight text-zinc-900 sm:text-5xl">
            Data sources &amp; credits
          </h1>
          <p className="mt-5 text-lg text-zinc-700 leading-relaxed">
            Every threshold we ship — the 12% drought floor, the dollar-spot
            pressure model, FAO-56 ET₀, the dewpoint band — traces back to a
            peer-reviewed paper or a public-domain government feed. This page
            lists every one of them, with the URL, the DOI, and what we use it
            for.
          </p>
          <p className="mt-3 text-base text-zinc-600">
            We don't believe in black boxes. If you can't cite it, you can't
            ship it.
          </p>
        </div>
      </section>

      {/* Peer-reviewed papers */}
      <section className="mx-auto max-w-4xl px-6 py-14" data-testid="credits-papers">
        <div className="flex items-center gap-2">
          <Sparkle size={26} weight="duotone" className="text-emerald-700" />
          <h2 className="text-3xl font-black text-zinc-900">Peer-reviewed papers</h2>
        </div>
        <p className="mt-2 text-zinc-700">
          The agronomic engine.
        </p>
        <ul className="mt-6 space-y-4">
          <Citation
            testid="cite-allen-1998"
            authors="Allen, R.G., Pereira, L.S., Raes, D., Smith, M."
            year="1998"
            title="Crop Evapotranspiration — Guidelines for Computing Crop Water Requirements (FAO Irrigation and Drainage Paper No. 56)"
            journal="Food and Agriculture Organization of the United Nations"
            url="https://www.fao.org/4/x0490e/x0490e00.htm"
            note="The reference for ET₀ (reference evapotranspiration). TurfPulse uses the FAO-56 Penman-Monteith equation as the spine of the daily water-budget model — temperature, humidity, wind, and solar radiation in, mm/day of crop water demand out."
          />
          <Citation
            testid="cite-smith-kerns-2018"
            authors="Smith, D.L., Kerns, J.P., Walker, N.R., Payne, A.F., Horvath, B., Inguagiato, J.C., et al."
            year="2018"
            title="Development and validation of a weather-based warning system to advise fungicide applications to control dollar spot on turfgrass"
            journal="PLOS ONE 13(3): e0194216"
            doi="10.1371/journal.pone.0194216"
            url="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0194216"
            note="The Smith-Kerns dollar-spot model — a logistic regression on 5-day moving average relative humidity and daily mean temperature — predicts dollar-spot probability. TurfPulse runs this model nightly off your local NWS data and surfaces the score on the disease-pressure tile. The original paper validated a 20% spray threshold that cut fungicide use by up to 30% versus calendar-based programs across seven states."
          />
          <Citation
            testid="cite-wherley-tamu"
            authors="Wherley, B.G. et al. (TAMU AgriLife Extension)"
            year="2014–2020"
            title="Soil moisture-based deficit irrigation thresholds for warm-season turfgrass cultivars on USGA-spec sand profiles"
            journal="Texas A&M AgriLife Extension — turfgrass research series"
            url="https://aggieturf.tamu.edu/"
            note="The empirical basis for TurfPulse's 12% SVWC drought-floor override on bermudagrass. Wherley and colleagues at TAMU repeatedly identified SVWC ≈ 10–12% as the wilt-stress threshold on coarse-textured fairway profiles, with cultivar-level differences (TifTuf and Tahoma 31 sustain quality further into the dry-down). When CH1 (the DRY-zone probe) drops below 12%, TurfPulse overrides skip-gates and fires anyway — except where freeze or municipal blackout windows take precedence."
          />
          <Citation
            testid="cite-christiansen-1942"
            authors="Christiansen, J.E."
            year="1942"
            title="Hydraulics of Sprinkling Systems for Irrigation (Bulletin 670)"
            journal="University of California Agricultural Experiment Station"
            url="https://oac.cdlib.org/findaid/ark:/13030/c8k64nzh/"
            note="The original Distribution Uniformity (DU) paper — the math that says a sprinkler delivers different rates across its arc, and the field method for measuring it with catch-cups. Step 5 of our methodology uses Christiansen's DU coefficient (~0.75 for residential pop-ups) to convert nominal precipitation rate into effective PR."
          />
          <Citation
            testid="cite-aps-dollar-spot"
            authors="Latin, R., et al."
            year="2021"
            title="Dollar Spot of Turfgrass: Biology, Epidemiology, and Management — Comprehensive Review"
            journal="Phytopathology — APS Journals"
            url="https://apsjournals.apsnet.org/doi/10.1094/PHYTO-11-20-0505-RVW"
            note="The contemporary review backing TurfPulse's dewpoint-band advisory. Prolonged leaf wetness (>8–12 hours) — driven by overnight dew, high relative humidity, and dewpoints inside the 60–70°F band — is the dominant epidemiological driver. The dewpoint tile uses this band as the disease-pressure trigger."
          />
          <Citation
            testid="cite-mu-ipm1029"
            authors="University of Missouri Extension"
            year="Latest revision"
            title="IPM1029: Identification and Management of Turfgrass Diseases"
            journal="MU Extension"
            url="https://extension.missouri.edu/publications/ipm1029"
            note="Extension-grade reference for pre-emergent timing windows, leaf-wetness reduction strategies, and the 'irrigate deeply and infrequently between midnight and early morning' rule that TurfPulse encodes in its predawn-fire bias."
          />
          <Citation
            testid="cite-uga-c891"
            authors="University of Georgia Extension"
            year="Latest revision"
            title="Circular 891: Turfgrass Pest Management Handbook"
            journal="UGA Cooperative Extension"
            url="https://fieldreport.caes.uga.edu/wp-content/uploads/2025/08/C-891_7.pdf"
            note="Cross-reference for warm-season disease pressure, fertility-by-season guidance, and the early-day dew-removal protocol that influences our 5:30 AM predawn anchor window."
          />
          <Citation
            testid="cite-ia-swat"
            authors="Irrigation Association"
            year="Ongoing"
            title="Smart Water Application Technologies (SWAT) Testing Protocols"
            journal="Irrigation Association"
            url="https://www.irrigation.org/IA/Resources/SWAT/IA/Resources/SWAT.aspx"
            note="The 0.75″/session safe-runoff cap in Step 4 of the methodology comes from IA SWAT field-testing protocols — anything over that ponds on clay or drains past the root zone on sand. Both wasted."
          />
        </ul>
      </section>

      {/* Public weather + soils feeds */}
      <section
        className="border-y border-zinc-200 bg-zinc-50/60"
        data-testid="credits-feeds"
      >
        <div className="mx-auto max-w-4xl px-6 py-14">
          <div className="flex items-center gap-2">
            <CloudRain size={26} weight="duotone" className="text-sky-700" />
            <h2 className="text-3xl font-black text-zinc-900">Live data feeds</h2>
          </div>
          <p className="mt-2 text-zinc-700">
            Government, public-domain, and licensed feeds we pull every 15 min.
          </p>
          <div className="mt-6 overflow-hidden rounded-lg border border-zinc-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-zinc-100 text-xs uppercase text-zinc-600">
                <tr>
                  <th className="px-4 py-2 text-left">Feed</th>
                  <th className="px-4 py-2 text-left">What we use it for</th>
                  <th className="px-4 py-2 text-left">License</th>
                  <th className="px-4 py-2 text-left">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                <FeedRow
                  testid="feed-nws"
                  name="NOAA / National Weather Service"
                  what="Authoritative US forecasts, freeze advisories, and severe-weather alerts. Used in skip-gate decisions and the freeze-window override."
                  license="Public domain (US Gov)"
                  url="https://www.weather.gov/documentation/services-web-api"
                />
                <FeedRow
                  testid="feed-openmeteo"
                  name="Open-Meteo"
                  what="Hourly forecast feed for ET₀ inputs (temp, RH, wind, solar) and the dewpoint band. Cached server-side; primary fallback when NWS is degraded."
                  license="CC-BY-4.0 (commercial OK)"
                  url="https://open-meteo.com/"
                />
                <FeedRow
                  testid="feed-openweather"
                  name="OpenWeather"
                  what="Backup forecast feed for redundancy and international coverage."
                  license="Commercial — paid tier"
                  url="https://openweathermap.org/api"
                />
                <FeedRow
                  testid="feed-wunderground"
                  name="Weather Underground PWS"
                  what="Personal Weather Station network — the local micro-station feed (KTXROUND935 in Round Rock) used to ground-truth the regional forecast."
                  license="Commercial — PWS API"
                  url="https://www.wunderground.com/about/data"
                />
                <FeedRow
                  testid="feed-ecowitt"
                  name="Ecowitt Cloud"
                  what="Soil moisture (WH51 / WH52), soil temperature (WN34), 7-in-1 weather (WS90), and lightning detector feeds direct from your gateway. The probe-driven irrigation engine reads off this feed every 15 min."
                  license="User-owned data via Ecowitt API"
                  url="https://api.ecowitt.net/api/v3/device/list"
                />
                <FeedRow
                  testid="feed-rachio"
                  name="Rachio Public API"
                  what="Zone state, schedule dispatch, and skip events. TurfPulse fires zones via authenticated POST and listens for completion via polling."
                  license="Commercial — Rachio Developer Program"
                  url="https://rachio.readme.io/"
                />
                <FeedRow
                  testid="feed-tamu"
                  name="TexasET Network (TAMU AgriLife)"
                  what="Texas-specific ET₀ baselines and crop coefficients (Kc) for warm-season turf. Used to ground-truth the FAO-56 calc against in-state stations."
                  license="Public — TAMU AgriLife"
                  url="https://texaset.tamu.edu/"
                />
                <FeedRow
                  testid="feed-usgs-soils"
                  name="USDA NRCS Web Soil Survey"
                  what="County-level soil texture profiles (sand/silt/clay %, available water capacity). Used to suggest realistic root-zone depths and runoff caps for new users."
                  license="Public domain (US Gov)"
                  url="https://websoilsurvey.nrcs.usda.gov/"
                />
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Models & equations */}
      <section className="mx-auto max-w-4xl px-6 py-14" data-testid="credits-models">
        <div className="flex items-center gap-2">
          <ChartBar size={26} weight="duotone" className="text-emerald-700" />
          <h2 className="text-3xl font-black text-zinc-900">Models &amp; equations</h2>
        </div>
        <p className="mt-2 text-zinc-700">The math, in one place.</p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Card className="border-emerald-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">
                Reference ET (ET₀)
              </div>
              <div className="mt-2 font-semibold text-zinc-900">FAO-56 Penman-Monteith</div>
              <p className="mt-2 text-xs text-zinc-700 leading-relaxed">
                Allen et al. 1998. Daily mm of crop water demand for a 0.12 m
                short-grass reference. Inputs: T, RH, wind at 2 m, net
                radiation. Drives the water-budget tile and FAO-56-aligned
                MAD calculations.
              </p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">
                Distribution Uniformity (DU)
              </div>
              <div className="mt-2 font-semibold text-zinc-900">Christiansen 1942</div>
              <p className="mt-2 text-xs text-zinc-700 leading-relaxed">
                The catch-cup math: <code>DU = 1 − (mean abs deviation / mean
                catch)</code>. Used to convert nominal nozzle PR into effective
                PR in Step 5 of the probe-driven runtime formula.
              </p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">
                Dollar-spot risk
              </div>
              <div className="mt-2 font-semibold text-zinc-900">Smith-Kerns 2018</div>
              <p className="mt-2 text-xs text-zinc-700 leading-relaxed">
                Logistic regression on 5-day moving avg RH + daily mean temp.
                Output is a 0–1 probability of dollar-spot occurrence —
                TurfPulse displays this as a 0–100 score with the 20% action
                threshold that the original paper validated.
              </p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">
                Growing Degree Days (GDD)
              </div>
              <div className="mt-2 font-semibold text-zinc-900">
                Modified base-50 (turf) and base-55 (PGR rebound)
              </div>
              <p className="mt-2 text-xs text-zinc-700 leading-relaxed">
                Daily accumulation of (mean temp − base), floored at 0. Drives
                pre-emergent windows, PGR re-application timing, and warm-season
                green-up alerts. We expose both base-50 and base-55 because the
                research literature uses both for different applications.
              </p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">
                MAD (Management Allowed Depletion)
              </div>
              <div className="mt-2 font-semibold text-zinc-900">FAO-56 §8.2</div>
              <p className="mt-2 text-xs text-zinc-700 leading-relaxed">
                The fraction of plant-available water (PAW) the root zone can
                lose before stress kicks in. We default to 0.50 for
                warm-season turf and gate fires when depletion exceeds it.
              </p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest font-bold text-emerald-700">
                Drought floor override
              </div>
              <div className="mt-2 font-semibold text-zinc-900">Wherley TAMU AgriLife</div>
              <p className="mt-2 text-xs text-zinc-700 leading-relaxed">
                Hard threshold at <strong>SVWC &lt; 12%</strong> on the DRY-zone
                probe forces a fire regardless of skip-gates (except freeze
                and municipal blackout, which always win).
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Honest limitations */}
      <section
        className="border-t border-zinc-200 bg-amber-50/40"
        data-testid="credits-limits"
      >
        <div className="mx-auto max-w-4xl px-6 py-14">
          <div className="flex items-center gap-2">
            <Drop size={26} weight="duotone" className="text-amber-700" />
            <h2 className="text-3xl font-black text-zinc-900">
              What we don't claim
            </h2>
          </div>
          <ul className="mt-5 space-y-3 text-zinc-700 list-disc pl-6">
            <li>
              <strong>FAO-56 is a reference crop, not your crop.</strong> ET₀
              is short-grass at full canopy. Crop coefficients (Kc) for your
              actual species + condition introduce error of ±10–20%. The probe
              is the corrective ground-truth.
            </li>
            <li>
              <strong>The 12% SVWC drought floor is bermuda-on-fine-sandy-loam.</strong>{' '}
              Cool-season grasses, organic-rich loam, and clay profiles
              behave differently. Inner Circle members can override the floor
              per cultivar.
            </li>
            <li>
              <strong>Smith-Kerns is a probability, not a prediction.</strong>{' '}
              A 90% score doesn't mean dollar spot tomorrow — it means the
              weather is suitable for it. Cultural pressure (mowing, dew, N
              status) still has to align.
            </li>
            <li>
              <strong>PWS data is as good as the station siting.</strong> A
              wunderground station next to an A/C exhaust will lie about RH.
              We surface station age + last-update so you can sanity-check.
            </li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-6 py-14">
        <Card className="border-emerald-300 bg-emerald-50/60">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-black text-zinc-900">
              Found a paper we should cite?
            </h3>
            <p className="mt-2 text-sm text-zinc-700">
              We update this page when new research lands. Email{' '}
              <a
                href="mailto:hello@turfpulse.app"
                className="text-emerald-700 underline font-semibold"
                data-testid="credits-email-link"
              >
                hello@turfpulse.app
              </a>{' '}
              with the DOI and we'll add it.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3 text-sm">
              <Link
                to="/methodology/probe-driven-irrigation"
                className="inline-flex items-center gap-1 rounded-md bg-emerald-700 px-4 py-2 font-semibold text-white hover:bg-emerald-800"
                data-testid="credits-cta-methodology"
              >
                See the methodology page
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-1 rounded-md border border-zinc-300 px-4 py-2 font-semibold text-zinc-800 hover:bg-zinc-100"
                data-testid="credits-cta-home"
              >
                Back to TurfPulse
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
